// Local mock adapter — answers exactly the request shapes http.js's real
// branch would send, against the in-memory db (db.js) using the rules in
// rules.js. One module-level `db` instance persists for the life of the
// tab (reset() below is for specs only).
//
// Every route handler returns `{ status, data }` on success or throws via
// fail(status, code, message), which request() lets propagate unchanged —
// so callers can't tell the mock and the real proxy apart.
import { seed, newId } from './db';
import {
  bedroomsLabel,
  computeRollup,
  openingHoursText,
  computeSlots,
  computeDates,
  autoAssignAgent,
  hasFutureAppointment,
  todayStr,
} from './rules';

let db = seed();

// Specs only — components/store never need to reset the mock mid-session.
export function resetMockDb() {
  db = seed();
}

const LATENCY_MS = 150;
function delay() {
  return new Promise(resolve => {
    setTimeout(resolve, LATENCY_MS);
  });
}

// axios itself throws an error whose `.response` property carries
// {status, data} — this makes the mock's rejection shape identical, so
// request()'s real and mock branches are indistinguishable to a catch()
// block. A plain `throw {response: ...}` literal would do the same thing
// but trips eslint's no-throw-literal; wrapping it in a real Error (with
// `.response` attached) is throwable without disabling the rule.
class MockHttpError extends Error {
  constructor(status, code, message) {
    super(message || code);
    this.response = { status, data: { detail: { code, message } } };
  }
}
function fail(status, code, message) {
  throw new MockHttpError(status, code, message || code);
}
function notFound(code) {
  fail(404, code, `${code} in mock`);
}

const STATUS_LABELS = {
  upcoming: 'Coming soon',
  open: 'Now open for booking',
  sold_out: 'Sold out',
  completed: 'Completed',
};
const APPT_STATUS_LABELS = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// ---------- serializers ----------

function serializeUnit(unit) {
  return {
    ...unit,
    bedrooms_label: bedroomsLabel(unit.bedrooms, unit.bedrooms_extra),
    bathrooms_label: bedroomsLabel(unit.bathrooms, unit.bathrooms_extra),
  };
}

function unitsForProperty(propertyId) {
  return db.units
    .filter(u => u.property_id === propertyId)
    .sort((a, b) => a.display_order - b.display_order);
}

function serializePropertyListItem(property) {
  const units = unitsForProperty(property.property_id);
  return {
    property_id: property.property_id,
    project_code: property.project_code,
    name: property.name,
    developer: property.developer,
    area: property.area,
    region: property.region,
    property_type: property.property_type,
    property_subtype: property.property_subtype,
    project_status: property.project_status,
    project_status_label: STATUS_LABELS[property.project_status],
    marketing_badge: property.marketing_badge,
    is_active: property.is_active,
    tenure: property.tenure,
    buyer_restriction: property.buyer_restriction,
    booking_mode: property.booking_mode,
    cover_image_url: db.cover[property.property_id]?.url || null,
    rollup: computeRollup(units),
    embedding_status: property.embedding_status,
    created_at: property.created_at,
    updated_at: property.updated_at,
  };
}

function serializePropertyDetail(property) {
  const units = unitsForProperty(property.property_id);
  const hours = db.operatingHours[property.property_id] || [];
  const assignedAgents = db.agents.filter(a =>
    property.agent_ids.includes(a.agent_id)
  );
  return {
    property_id: property.property_id,
    project_code: property.project_code,
    name: property.name,
    developer: property.developer,
    short_description: property.short_description,
    long_description: property.long_description,
    property_type: property.property_type,
    property_subtype: property.property_subtype,
    project_status: property.project_status,
    project_status_label: STATUS_LABELS[property.project_status],
    marketing_badge: property.marketing_badge,
    is_active: property.is_active,
    currency: property.currency,
    website_url: property.website_url,
    area: property.area,
    region: property.region,
    address: property.address,
    latitude: property.latitude,
    longitude: property.longitude,
    tenure: property.tenure,
    buyer_restriction: property.buyer_restriction,
    showroom: {
      address: property.showroom_address,
      phone: property.showroom_phone,
      waze_url: property.showroom_waze_url,
      maps_url: property.showroom_maps_url,
      opening_hours_text: openingHoursText(hours),
      opening_hours: hours,
    },
    booking_mode: property.booking_mode,
    viewing_duration_minutes: property.viewing_duration_minutes,
    slot_capacity: property.slot_capacity,
    timezone: property.timezone,
    rollup: computeRollup(units),
    features: db.features[property.property_id] || [],
    nearby: db.nearby[property.property_id] || [],
    gallery: db.gallery[property.property_id] || [],
    brochures: db.brochures[property.property_id] || [],
    cover_image_url: db.cover[property.property_id]?.url || null,
    units: units.map(serializeUnit),
    agents: assignedAgents.map(a => ({ agent_id: a.agent_id, name: a.name })),
    embedding_status: property.embedding_status,
    created_by: property.created_by,
    created_at: property.created_at,
    updated_at: property.updated_at,
  };
}

function serializeAppointment(appt) {
  const property = db.properties.find(p => p.property_id === appt.property_id);
  const unit = appt.unit_id
    ? db.units.find(u => u.unit_id === appt.unit_id)
    : null;
  const agent = appt.agent_id
    ? db.agents.find(a => a.agent_id === appt.agent_id)
    : null;
  return {
    appointment_id: appt.appointment_id,
    appointment_type: appt.appointment_type,
    // The row's own persisted shape, not the property's current (mutable)
    // booking_mode — see the PATCH handler's comment above for why.
    booking_mode: appt.end_time !== null ? 'agent' : 'walk_in',
    status: appt.status,
    status_label: APPT_STATUS_LABELS[appt.status],
    date: appt.date,
    start_time: appt.start_time,
    end_time: appt.end_time,
    duration_minutes: appt.duration_minutes,
    timezone: property?.timezone || 'Asia/Kuala_Lumpur',
    property: property
      ? {
          property_id: property.property_id,
          name: property.name,
          deleted: false,
        }
      : null,
    unit: unit
      ? {
          unit_id: unit.unit_id,
          floor_plan_code: unit.floor_plan_code,
          deleted: false,
        }
      : null,
    agent: agent
      ? { agent_id: agent.agent_id, name: agent.name, phone: agent.phone }
      : null,
    agent_auto_assigned: appt.agent_auto_assigned,
    customer: {
      customer_id: appt.customer_id,
      name: appt.customer_name,
      phone_number: appt.customer_phone,
    },
    conversation_id: appt.conversation_id,
    location_type: appt.location_type,
    location_note: appt.location_note,
    source: appt.source,
    customer_notes: appt.customer_notes,
    agent_notes: appt.agent_notes,
    handover_checklist: appt.handover_checklist,
    before_reschedule_metadata: appt.before_reschedule_metadata,
    cancelled_at: appt.cancelled_at,
    cancellation_reason: appt.cancellation_reason,
    completed_at: appt.completed_at,
    created_by: appt.created_by,
    created_at: appt.created_at,
    updated_at: appt.updated_at,
  };
}

// ---------- shared helpers ----------

function paginate(items, params) {
  const page = Number(params?.page) || 1;
  const pageSize = Math.min(Number(params?.page_size) || 20, 100);
  const start = (page - 1) * pageSize;
  return {
    page,
    page_size: pageSize,
    total: items.length,
    results: items.slice(start, start + pageSize),
  };
}

function findProperty(pid) {
  const property = db.properties.find(p => p.property_id === pid);
  if (!property) notFound('property_not_found');
  return property;
}
function findUnit(pid, unitId) {
  const unit = db.units.find(
    u => u.unit_id === unitId && u.property_id === pid
  );
  if (!unit) notFound('unit_not_found');
  return unit;
}
function findAgent(agentId) {
  const agent = db.agents.find(a => a.agent_id === agentId);
  if (!agent) notFound('agent_not_found');
  return agent;
}
function findAppointment(id) {
  const appt = db.appointments.find(a => a.appointment_id === id);
  if (!appt) notFound('appointment_not_found');
  return appt;
}

function assertUniqueProjectCode(code, excludePid) {
  const taken = db.properties.some(
    p =>
      p.property_id !== excludePid &&
      p.project_code.toLowerCase() === String(code).toLowerCase()
  );
  if (taken) fail(409, 'project_code_taken', 'Project code already exists');
}
function assertUniqueFloorPlanCode(pid, code, excludeUnitId) {
  const taken = db.units.some(
    u =>
      u.property_id === pid &&
      u.unit_id !== excludeUnitId &&
      u.floor_plan_code.toLowerCase() === String(code).toLowerCase()
  );
  if (taken)
    fail(409, 'floor_plan_code_taken', 'Floor plan code already exists');
}

// `allowSvg` only for feature/nearby icons — every other slot refuses SVG.
// `brochure` takes PDF/JPG/PNG (no WebP) with its own 415 code.
function extractFile(data, { allowSvg = false, brochure = false } = {}) {
  // `data` is a FormData built by the caller (media.js) with a `file`
  // field holding a real File/Blob.
  const file = data instanceof FormData ? data.get('file') : null;
  if (!file) fail(422, 'no_files', 'No file provided');
  const okTypes = brochure
    ? ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
    : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (allowSvg) okTypes.push('image/svg+xml');
  if (file.type && !okTypes.includes(file.type)) {
    if (brochure) {
      fail(415, 'unsupported_brochure_type', 'Unsupported brochure type');
    }
    fail(415, 'unsupported_image_type', 'Unsupported image type');
  }
  if (file.size && file.size > 10 * 1024 * 1024) {
    fail(413, 'file_too_large', 'File too large');
  }
  return { file, url: URL.createObjectURL(file) };
}

// ---------- route table ----------
// {method, pattern} — pattern segments starting with ':' are captured as
// params. Handlers get (params, { query, body }) and return { status, data }.

function compile(pattern) {
  const keys = [];
  const regex = new RegExp(
    `^${pattern
      .split('/')
      .map(seg => {
        if (seg.startsWith(':')) {
          keys.push(seg.slice(1));
          return '([^/]+)';
        }
        return seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('/')}$`
  );
  return { regex, keys };
}

const routes = [];
function route(method, pattern, handler) {
  routes.push({ method, ...compile(pattern), handler });
}

// -- config --
route('get', '/config', () => ({
  status: 200,
  data: {
    module_enabled: true,
    timezone: 'Asia/Kuala_Lumpur',
    properties_count: db.properties.length,
    agents_count: db.agents.length,
  },
}));

// -- properties: list/create --
route('get', '/properties', (params, { query }) => {
  let list = db.properties.slice();
  if (query.q) {
    const q = query.q.toLowerCase();
    list = list.filter(
      p => p.name.toLowerCase().includes(q) || p.area.toLowerCase().includes(q)
    );
  }
  if (query.property_type)
    list = list.filter(p => p.property_type === query.property_type);
  if (query.region) list = list.filter(p => p.region === query.region);
  if (query.project_status)
    list = list.filter(p => p.project_status === query.project_status);
  if (query.active === 'active') list = list.filter(p => p.is_active);
  if (query.active === 'inactive') list = list.filter(p => !p.is_active);
  if (query.min_price || query.max_price) {
    const lo = Number(query.min_price) || 0;
    const hi = Number(query.max_price) || Infinity;
    list = list.filter(p => {
      const rollup = computeRollup(unitsForProperty(p.property_id));
      if (rollup.mixed_currency || rollup.price_min == null) return false;
      return rollup.price_max >= lo && rollup.price_min <= hi;
    });
  }
  const sortBy = query.sort_by || 'created_at';
  const order = query.sort_order === 'asc' ? 1 : -1;
  list.sort((a, b) => {
    if (sortBy === 'name') return order * a.name.localeCompare(b.name);
    if (sortBy === 'price_min') {
      const ra = computeRollup(unitsForProperty(a.property_id)).price_min ?? 0;
      const rb = computeRollup(unitsForProperty(b.property_id)).price_min ?? 0;
      return order * (ra - rb);
    }
    return order * a.created_at.localeCompare(b.created_at);
  });
  const page = paginate(list, query);
  return {
    status: 200,
    data: { ...page, results: page.results.map(serializePropertyListItem) },
  };
});

route('post', '/properties', (params, { body }) => {
  if (!body?.name || !body?.property_type)
    fail(422, 'validation', 'name/property_type required');
  if (body.project_code) assertUniqueProjectCode(body.project_code);
  const now = new Date().toISOString();
  const property = {
    property_id: newId('property'),
    project_code: body.project_code || `NEW${db.properties.length + 1}`,
    name: body.name,
    developer: body.developer || '',
    short_description: body.short_description || '',
    long_description: body.long_description || '',
    property_type: body.property_type,
    property_subtype: body.property_subtype || '',
    project_status: 'upcoming',
    marketing_badge: body.marketing_badge || null,
    is_active: true,
    currency: body.currency || 'RM',
    website_url: body.website_url || '',
    area: '',
    region: '',
    address: '',
    latitude: null,
    longitude: null,
    tenure: 'Freehold',
    buyer_restriction: null,
    showroom_address: '',
    showroom_phone: '',
    showroom_waze_url: '',
    showroom_maps_url: '',
    booking_mode: 'walk_in',
    viewing_duration_minutes: null,
    slot_capacity: null,
    timezone: 'Asia/Kuala_Lumpur',
    embedding_status: 'pending',
    created_by: null,
    created_at: now,
    updated_at: now,
    agent_ids: [],
  };
  db.properties.push(property);
  db.operatingHours[property.property_id] = [];
  db.operatingHoursOverrides[property.property_id] = [];
  db.features[property.property_id] = [];
  db.nearby[property.property_id] = [];
  db.gallery[property.property_id] = [];
  db.cover[property.property_id] = null;
  return { status: 201, data: serializePropertyDetail(property) };
});

// -- properties: detail/patch/delete --
route('get', '/properties/regions', () => ({
  status: 200,
  data: {
    regions: [
      ...new Set(db.properties.map(p => p.region).filter(Boolean)),
    ].sort(),
  },
}));

route('get', '/properties/:pid', params => ({
  status: 200,
  data: serializePropertyDetail(findProperty(params.pid)),
}));

route('patch', '/properties/:pid', (params, { body }) => {
  const property = findProperty(params.pid);
  if (body.project_code)
    assertUniqueProjectCode(body.project_code, property.property_id);
  if (body.booking_mode === 'agent') {
    const duration =
      body.viewing_duration_minutes ?? property.viewing_duration_minutes;
    if (!duration)
      fail(
        422,
        'viewing_duration_required',
        'Viewing duration required for agent mode'
      );
  }
  Object.assign(property, body, { updated_at: new Date().toISOString() });
  return { status: 200, data: serializePropertyDetail(property) };
});

route('put', '/properties/:pid/status', (params, { body }) => {
  const property = findProperty(params.pid);
  property.project_status = body.project_status;
  return {
    status: 200,
    data: {
      property_id: property.property_id,
      project_status: property.project_status,
      project_status_label: STATUS_LABELS[property.project_status],
    },
  };
});

route('put', '/properties/:pid/active', (params, { body }) => {
  const property = findProperty(params.pid);
  property.is_active = !!body.is_active;
  return {
    status: 200,
    data: { property_id: property.property_id, is_active: property.is_active },
  };
});

route('post', '/properties/:pid/embedding', (params, { body }) => {
  const property = findProperty(params.pid);
  if (!body?.force && property.embedding_status === 'ready') {
    return {
      status: 202,
      data: {
        property_id: property.property_id,
        embedding_status: 'ready',
        queued: false,
      },
    };
  }
  property.embedding_status = 'pending';
  return {
    status: 202,
    data: {
      property_id: property.property_id,
      embedding_status: 'pending',
      queued: true,
    },
  };
});

route('delete', '/properties/:pid', params => {
  const property = findProperty(params.pid);
  if (hasFutureAppointment(db, a => a.property_id === property.property_id)) {
    fail(409, 'has_appointments', 'Upcoming appointments exist');
  }
  db.properties = db.properties.filter(
    p => p.property_id !== property.property_id
  );
  db.units = db.units.filter(u => u.property_id !== property.property_id);
  return { status: 204, data: null };
});

// -- features --
route('get', '/properties/:pid/features', params => {
  findProperty(params.pid);
  return { status: 200, data: { results: db.features[params.pid] || [] } };
});
route('post', '/properties/:pid/features', (params, { body }) => {
  findProperty(params.pid);
  const list = db.features[params.pid] || (db.features[params.pid] = []);
  if (
    list.some(f => f.name.toLowerCase() === String(body.name).toLowerCase())
  ) {
    fail(409, 'amenity_name_taken', 'Feature already listed');
  }
  const feature = {
    amenity_id: newId('amenity'),
    name: body.name,
    icon_url: null,
    display_order: list.length,
  };
  list.push(feature);
  return { status: 201, data: feature };
});
route('delete', '/properties/:pid/features/:fid', params => {
  const list = db.features[params.pid] || [];
  if (!list.some(f => f.amenity_id === params.fid))
    notFound('amenity_not_found');
  db.features[params.pid] = list.filter(f => f.amenity_id !== params.fid);
  return { status: 204, data: null };
});
route('put', '/properties/:pid/features/:fid/icon', (params, { body }) => {
  const list = db.features[params.pid] || [];
  const feature = list.find(f => f.amenity_id === params.fid);
  if (!feature) notFound('amenity_not_found');
  feature.icon_url = extractFile(body, { allowSvg: true }).url;
  return { status: 200, data: feature };
});
route('delete', '/properties/:pid/features/:fid/icon', params => {
  const list = db.features[params.pid] || [];
  const feature = list.find(f => f.amenity_id === params.fid);
  if (!feature) notFound('amenity_not_found');
  feature.icon_url = null;
  return { status: 204, data: null };
});

// -- nearby --
route('get', '/properties/:pid/nearby', params => {
  findProperty(params.pid);
  return { status: 200, data: { results: db.nearby[params.pid] || [] } };
});
route('post', '/properties/:pid/nearby', (params, { body }) => {
  findProperty(params.pid);
  const list = db.nearby[params.pid] || (db.nearby[params.pid] = []);
  if (
    list.some(n => n.name.toLowerCase() === String(body.name).toLowerCase())
  ) {
    fail(409, 'amenity_name_taken', 'Nearby place already listed');
  }
  const distanceKm =
    body.distance_unit === 'mi'
      ? body.distance_value * 1.609344
      : body.distance_value;
  const nearby = {
    amenity_id: newId('amenity'),
    name: body.name,
    distance_value: body.distance_value,
    distance_unit: body.distance_unit || 'km',
    distance_km: Number(distanceKm.toFixed(2)),
    maps_url: body.maps_url || null,
    icon_url: null,
    display_order: list.length,
  };
  list.push(nearby);
  return { status: 201, data: nearby };
});
route('delete', '/properties/:pid/nearby/:nid', params => {
  const list = db.nearby[params.pid] || [];
  if (!list.some(n => n.amenity_id === params.nid))
    notFound('amenity_not_found');
  db.nearby[params.pid] = list.filter(n => n.amenity_id !== params.nid);
  return { status: 204, data: null };
});
route('put', '/properties/:pid/nearby/:nid/icon', (params, { body }) => {
  const list = db.nearby[params.pid] || [];
  const nearby = list.find(n => n.amenity_id === params.nid);
  if (!nearby) notFound('amenity_not_found');
  nearby.icon_url = extractFile(body, { allowSvg: true }).url;
  return { status: 200, data: nearby };
});
route('delete', '/properties/:pid/nearby/:nid/icon', params => {
  const list = db.nearby[params.pid] || [];
  const nearby = list.find(n => n.amenity_id === params.nid);
  if (!nearby) notFound('amenity_not_found');
  nearby.icon_url = null;
  return { status: 204, data: null };
});

// -- cover / gallery --
route('put', '/properties/:pid/cover', (params, { body }) => {
  findProperty(params.pid);
  const { url } = extractFile(body);
  const cover = {
    image_id: newId('image'),
    url,
    caption: null,
    display_order: 0,
  };
  db.cover[params.pid] = cover;
  return { status: 200, data: cover };
});
route('delete', '/properties/:pid/cover', params => {
  findProperty(params.pid);
  db.cover[params.pid] = null;
  return { status: 204, data: null };
});
route('post', '/properties/:pid/gallery', (params, { body }) => {
  findProperty(params.pid);
  const { url } = extractFile(body);
  const list = db.gallery[params.pid] || (db.gallery[params.pid] = []);
  const image = {
    image_id: newId('image'),
    url,
    caption: body instanceof FormData ? body.get('caption') || null : null,
    display_order: list.length,
  };
  list.push(image);
  return { status: 201, data: image };
});
route('patch', '/properties/:pid/gallery/:iid', (params, { body }) => {
  const list = db.gallery[params.pid] || [];
  const image = list.find(i => i.image_id === params.iid);
  if (!image) notFound('image_not_found');
  if (body.caption !== undefined) image.caption = body.caption;
  return { status: 200, data: image };
});
route('delete', '/properties/:pid/gallery/:iid', params => {
  const list = db.gallery[params.pid] || [];
  if (!list.some(i => i.image_id === params.iid)) notFound('image_not_found');
  db.gallery[params.pid] = list.filter(i => i.image_id !== params.iid);
  return { status: 204, data: null };
});
route('put', '/properties/:pid/gallery/order', (params, { body }) => {
  const list = db.gallery[params.pid] || [];
  const ids = body.image_ids || [];
  if (
    ids.length !== list.length ||
    !ids.every(id => list.some(i => i.image_id === id))
  ) {
    fail(422, 'image_ids_mismatch', 'image_ids does not match the gallery');
  }
  const byId = Object.fromEntries(list.map(i => [i.image_id, i]));
  const reordered = ids.map((id, index) => ({
    ...byId[id],
    display_order: index,
  }));
  db.gallery[params.pid] = reordered;
  return { status: 200, data: { results: reordered } };
});

// -- brochures (13-fe-update §1) --
route('post', '/properties/:pid/brochures', (params, { body }) => {
  findProperty(params.pid);
  const { file, url } = extractFile(body, { brochure: true });
  const list = db.brochures[params.pid] || (db.brochures[params.pid] = []);
  const title = body instanceof FormData ? body.get('title') : null;
  const brochure = {
    brochure_id: newId('brochure'),
    title: title && title.trim() ? title.trim() : null,
    file_name: file.name || 'brochure',
    content_type: file.type || 'application/pdf',
    file_size: file.size || 0,
    url,
    display_order: list.length,
  };
  list.push(brochure);
  return { status: 201, data: brochure };
});
route('put', '/properties/:pid/brochures/order', (params, { body }) => {
  const list = db.brochures[params.pid] || [];
  const ids = body.brochure_ids || [];
  if (
    ids.length !== list.length ||
    new Set(ids).size !== ids.length ||
    !ids.every(id => list.some(b => b.brochure_id === id))
  ) {
    fail(422, 'brochure_ids_mismatch', 'brochure_ids does not match');
  }
  const byId = Object.fromEntries(list.map(b => [b.brochure_id, b]));
  const reordered = ids.map((id, index) => ({
    ...byId[id],
    display_order: index,
  }));
  db.brochures[params.pid] = reordered;
  return { status: 200, data: { results: reordered } };
});
route('patch', '/properties/:pid/brochures/:bid', (params, { body }) => {
  const list = db.brochures[params.pid] || [];
  const brochure = list.find(b => b.brochure_id === params.bid);
  if (!brochure) notFound('brochure_not_found');
  const title = body.title == null ? '' : String(body.title).trim();
  if (title.length > 100) fail(422, 'validation', 'title too long');
  brochure.title = title || null;
  return { status: 200, data: brochure };
});
route('delete', '/properties/:pid/brochures/:bid', params => {
  const list = db.brochures[params.pid] || [];
  if (!list.some(b => b.brochure_id === params.bid)) {
    notFound('brochure_not_found');
  }
  db.brochures[params.pid] = list
    .filter(b => b.brochure_id !== params.bid)
    .map((b, index) => ({ ...b, display_order: index }));
  return { status: 204, data: null };
});

// -- units --
route('get', '/properties/:pid/units', params => {
  findProperty(params.pid);
  return {
    status: 200,
    data: { results: unitsForProperty(params.pid).map(serializeUnit) },
  };
});
route('post', '/properties/:pid/units', (params, { body }) => {
  findProperty(params.pid);
  // built_up_size is optional in the floor plan form. The real BE's create
  // contract still lists it as required (13-fe-update-2026-09-30.md) — the
  // mock follows the form until that's confirmed.
  if (!body?.floor_plan_code || body.price_from == null) {
    fail(422, 'validation', 'floor_plan_code/price_from required');
  }
  assertUniqueFloorPlanCode(params.pid, body.floor_plan_code);
  const property = findProperty(params.pid);
  const now = new Date().toISOString();
  const unit = {
    unit_id: newId('unit'),
    property_id: params.pid,
    floor_plan_code: body.floor_plan_code,
    display_order: unitsForProperty(params.pid).length + 1,
    built_up_size: body.built_up_size,
    built_up_unit: body.built_up_unit || 'sqft',
    bedrooms: body.bedrooms ?? null,
    bedrooms_extra: body.bedrooms_extra ?? null,
    bathrooms: body.bathrooms ?? null,
    bathrooms_extra: body.bathrooms_extra ?? null,
    parking_lots: body.parking_lots ?? null,
    price_from: body.price_from,
    currency: body.currency || property.currency,
    website_url: body.website_url || '',
    layout_url: null,
    image_url: null,
    created_at: now,
    updated_at: now,
  };
  db.units.push(unit);
  return { status: 201, data: serializeUnit(unit) };
});
route('patch', '/properties/:pid/units/:uid', (params, { body }) => {
  const unit = findUnit(params.pid, params.uid);
  if (body.floor_plan_code)
    assertUniqueFloorPlanCode(params.pid, body.floor_plan_code, unit.unit_id);
  Object.assign(unit, body, { updated_at: new Date().toISOString() });
  return { status: 200, data: serializeUnit(unit) };
});
route('delete', '/properties/:pid/units/:uid', params => {
  const unit = findUnit(params.pid, params.uid);
  if (hasFutureAppointment(db, a => a.unit_id === unit.unit_id)) {
    fail(
      409,
      'has_appointments',
      'Upcoming appointments reference this floor plan'
    );
  }
  db.units = db.units.filter(u => u.unit_id !== unit.unit_id);
  return { status: 204, data: null };
});
route('put', '/properties/:pid/units/order', (params, { body }) => {
  const list = unitsForProperty(params.pid);
  const ids = body.unit_ids || [];
  if (
    ids.length !== list.length ||
    !ids.every(id => list.some(u => u.unit_id === id))
  ) {
    fail(
      422,
      'unit_ids_mismatch',
      'unit_ids does not match this project’s floor plans'
    );
  }
  ids.forEach((id, index) => {
    db.units.find(u => u.unit_id === id).display_order = index + 1;
  });
  return {
    status: 200,
    data: { results: unitsForProperty(params.pid).map(serializeUnit) },
  };
});
route('put', '/properties/:pid/units/:uid/layout', (params, { body }) => {
  const unit = findUnit(params.pid, params.uid);
  unit.layout_url = extractFile(body).url;
  return { status: 200, data: serializeUnit(unit) };
});
route('delete', '/properties/:pid/units/:uid/layout', params => {
  const unit = findUnit(params.pid, params.uid);
  unit.layout_url = null;
  return { status: 204, data: null };
});
route('put', '/properties/:pid/units/:uid/image', (params, { body }) => {
  const unit = findUnit(params.pid, params.uid);
  unit.image_url = extractFile(body).url;
  return { status: 200, data: serializeUnit(unit) };
});
route('delete', '/properties/:pid/units/:uid/image', params => {
  const unit = findUnit(params.pid, params.uid);
  unit.image_url = null;
  return { status: 204, data: null };
});

// -- agents --
route('get', '/properties/agents', (params, { query }) => {
  let list = db.agents.slice();
  if (query.q) {
    const q = query.q.toLowerCase();
    list = list.filter(
      a =>
        a.name.toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.phone || '').includes(q)
    );
  }
  if (query.property_id) {
    const property = findProperty(query.property_id);
    list = list.filter(a => property.agent_ids.includes(a.agent_id));
  }
  const page = paginate(list, query);
  return {
    status: 200,
    data: {
      ...page,
      results: page.results.map(a => ({
        ...a,
        property_ids: db.properties
          .filter(p => p.agent_ids.includes(a.agent_id))
          .map(p => p.property_id),
      })),
    },
  };
});
route('post', '/properties/agents', (params, { body }) => {
  if (!body?.name) fail(422, 'validation', 'name required');
  const now = new Date().toISOString();
  const agent = {
    agent_id: newId('agent'),
    name: body.name,
    email: body.email || '',
    phone: body.phone || '',
    created_at: now,
    updated_at: now,
  };
  db.agents.push(agent);
  return { status: 201, data: { ...agent, property_ids: [] } };
});
route('get', '/properties/agents/:aid', params => {
  const agent = findAgent(params.aid);
  return {
    status: 200,
    data: {
      ...agent,
      property_ids: db.properties
        .filter(p => p.agent_ids.includes(agent.agent_id))
        .map(p => p.property_id),
    },
  };
});
route('patch', '/properties/agents/:aid', (params, { body }) => {
  const agent = findAgent(params.aid);
  Object.assign(agent, body, { updated_at: new Date().toISOString() });
  return {
    status: 200,
    data: {
      ...agent,
      property_ids: db.properties
        .filter(p => p.agent_ids.includes(agent.agent_id))
        .map(p => p.property_id),
    },
  };
});
route('delete', '/properties/agents/:aid', params => {
  const agent = findAgent(params.aid);
  if (hasFutureAppointment(db, a => a.agent_id === agent.agent_id)) {
    fail(
      409,
      'has_appointments',
      'Upcoming appointments assigned to this agent'
    );
  }
  db.agents = db.agents.filter(a => a.agent_id !== agent.agent_id);
  db.properties.forEach(p => {
    p.agent_ids = p.agent_ids.filter(id => id !== agent.agent_id);
  });
  return { status: 204, data: null };
});
route('put', '/properties/:pid/agents', (params, { body }) => {
  const property = findProperty(params.pid);
  const ids = body.agent_ids || [];
  if (!ids.every(id => db.agents.some(a => a.agent_id === id))) {
    fail(422, 'unknown_agent_ids', 'Unknown agent id');
  }
  property.agent_ids = ids;
  return {
    status: 200,
    data: {
      property_id: property.property_id,
      agents: db.agents
        .filter(a => ids.includes(a.agent_id))
        .map(a => ({ agent_id: a.agent_id, name: a.name })),
    },
  };
});

// -- operating hours (Layer 0) --
route('get', '/properties/:pid/operating-hours', params => {
  const property = findProperty(params.pid);
  const hours = db.operatingHours[params.pid] || [];
  return {
    status: 200,
    data: {
      property_id: property.property_id,
      timezone: property.timezone,
      hours,
      opening_hours_text: openingHoursText(hours),
    },
  };
});
route('put', '/properties/:pid/operating-hours', (params, { body }) => {
  const property = findProperty(params.pid);
  const rows = body.hours || [];
  rows.forEach(r => {
    if (r.start_time >= r.end_time)
      fail(422, 'invalid_time_range', 'end_time must be after start_time');
  });
  const byDay = {};
  rows.forEach(r => {
    byDay[r.day_of_week] = byDay[r.day_of_week] || [];
    byDay[r.day_of_week].forEach(existing => {
      if (
        r.start_time < existing.end_time &&
        r.end_time > existing.start_time
      ) {
        fail(422, 'overlapping_hours', 'Rows on the same day overlap');
      }
    });
    byDay[r.day_of_week].push(r);
  });
  const withIds = rows.map(r => ({ id: newId('hour'), ...r }));
  db.operatingHours[params.pid] = withIds;
  return {
    status: 200,
    data: {
      property_id: property.property_id,
      timezone: property.timezone,
      hours: withIds,
      opening_hours_text: openingHoursText(withIds),
    },
  };
});

// -- availability --
function availabilityHandler(params, { query }) {
  const property = findProperty(params.pid);
  const appointmentType = query.appointment_type || 'viewing';
  if (appointmentType === 'key_handover' && !query.duration_minutes) {
    fail(
      422,
      'duration_required',
      'duration_minutes required for key_handover'
    );
  }
  if (appointmentType === 'viewing' && query.duration_minutes) {
    fail(
      422,
      'duration_not_allowed',
      'duration_minutes not allowed for a viewing'
    );
  }
  if (
    query.agent_id &&
    appointmentType === 'viewing' &&
    property.booking_mode === 'walk_in'
  ) {
    fail(
      422,
      'agent_not_applicable',
      'agent_id not applicable to a walk-in viewing'
    );
  }
  return {
    property,
    appointmentType,
    durationMinutes: query.duration_minutes
      ? Number(query.duration_minutes)
      : undefined,
    agentId: query.agent_id || undefined,
  };
}

route('get', '/properties/:pid/availability/dates', (params, { query }) => {
  const opts = availabilityHandler(params, { query });
  const fromDate = query.from_date;
  const toDate = query.to_date || fromDate;
  const result = computeDates(
    db,
    {
      propertyId: opts.property.property_id,
      appointmentType: opts.appointmentType,
      durationMinutes: opts.durationMinutes,
      agentId: opts.agentId,
    },
    fromDate,
    toDate
  );
  return {
    status: 200,
    data: {
      timezone: opts.property.timezone,
      booking_mode: opts.property.booking_mode,
      appointment_type: opts.appointmentType,
      duration_minutes:
        opts.appointmentType === 'key_handover'
          ? opts.durationMinutes
          : opts.property.viewing_duration_minutes,
      ...result,
    },
  };
});

route('get', '/properties/:pid/availability/slots', (params, { query }) => {
  const opts = availabilityHandler(params, { query });
  const result = computeSlots(db, {
    propertyId: opts.property.property_id,
    appointmentType: opts.appointmentType,
    durationMinutes: opts.durationMinutes,
    agentId: opts.agentId,
    dateStr: query.date,
  });
  return {
    status: 200,
    data: {
      timezone: opts.property.timezone,
      booking_mode: opts.property.booking_mode,
      appointment_type: opts.appointmentType,
      duration_minutes:
        opts.appointmentType === 'key_handover'
          ? opts.durationMinutes
          : opts.property.viewing_duration_minutes,
      slot_capacity:
        opts.appointmentType === 'viewing' ? opts.property.slot_capacity : null,
      ...result,
    },
  };
});

// -- appointments --
route('get', '/properties/appointments', (params, { query }) => {
  let list = db.appointments.slice();
  const today = todayStr();
  if (query.view === 'upcoming') {
    list = list.filter(
      a =>
        a.date >= today &&
        ['requested', 'confirmed', 'rescheduled'].includes(a.status)
    );
  } else if (query.view === 'archive') {
    list = list.filter(
      a => a.date < today || ['completed', 'cancelled'].includes(a.status)
    );
  }
  if (query.status) list = list.filter(a => a.status === query.status);
  if (query.appointment_type)
    list = list.filter(a => a.appointment_type === query.appointment_type);
  if (query.property_id)
    list = list.filter(a => a.property_id === query.property_id);
  if (query.unit_id) list = list.filter(a => a.unit_id === query.unit_id);
  if (query.agent_id) list = list.filter(a => a.agent_id === query.agent_id);
  if (query.customer_id)
    list = list.filter(
      a => String(a.customer_id) === String(query.customer_id)
    );
  if (query.conversation_id)
    list = list.filter(
      a => String(a.conversation_id) === String(query.conversation_id)
    );
  list.sort((a, b) =>
    query.view === 'archive'
      ? b.date.localeCompare(a.date)
      : a.date.localeCompare(b.date)
  );

  const statusCounts = {
    requested: 0,
    confirmed: 0,
    rescheduled: 0,
    completed: 0,
    cancelled: 0,
  };
  db.appointments.forEach(a => {
    if (
      (!query.property_id || a.property_id === query.property_id) &&
      (!query.appointment_type || a.appointment_type === query.appointment_type)
    ) {
      statusCounts[a.status] += 1;
    }
  });

  const page = paginate(list, query);
  return {
    status: 200,
    data: {
      ...page,
      status_counts: statusCounts,
      results: page.results.map(serializeAppointment),
    },
  };
});

// `availableAgentIds` is the slot's own `available_agent_ids` (already
// narrowed to agents free for that exact time) — auto-assign must pick
// from that pool, not from every agent assigned to the project, or it can
// hand back an agent who isn't actually free at this slot. `dateStr` is
// the appointment's own date, for "fewest appointments that day".
function resolveAgentForCreate(property, body, availableAgentIds, dateStr) {
  if (body.agent_id) {
    if (!property.agent_ids.includes(body.agent_id)) {
      fail(
        422,
        'agent_not_assigned_to_property',
        'Agent is not assigned to this project'
      );
    }
    return body.agent_id;
  }
  if (body.auto_assign_agent) {
    const agentId = autoAssignAgent(db, availableAgentIds, dateStr);
    if (!agentId)
      fail(409, 'agent_unavailable', 'No agent is free for this slot');
    return agentId;
  }
  return null;
}

route('post', '/properties/appointments', (params, { body }) => {
  const property = findProperty(body.property_id);
  const type = body.appointment_type;
  if (!type || !body.customer_id || !body.date || !body.start_time) {
    fail(
      422,
      'validation',
      'appointment_type/customer_id/date/start_time required'
    );
  }
  if (type === 'key_handover' && !body.unit_id)
    fail(422, 'unit_required', 'unit_id required for key_handover');
  if (body.unit_id) findUnit(property.property_id, body.unit_id);

  const isAgentBased =
    type === 'key_handover' || property.booking_mode === 'agent';
  if (type === 'key_handover' && !body.duration_minutes) {
    fail(
      422,
      'duration_required',
      'duration_minutes required for key_handover'
    );
  }
  if (
    type === 'viewing' &&
    property.booking_mode === 'agent' &&
    body.duration_minutes
  ) {
    fail(
      422,
      'duration_not_allowed',
      'duration_minutes not allowed for an agent-mode viewing'
    );
  }
  if (!isAgentBased && (body.agent_id || body.auto_assign_agent)) {
    fail(
      422,
      'agent_not_applicable',
      'agent_id not applicable to a walk-in viewing'
    );
  }
  if (isAgentBased && !body.agent_id && !body.auto_assign_agent) {
    fail(422, 'agent_choice_required', 'Pick an agent or Auto-assign');
  }
  if (isAgentBased && body.agent_id && body.auto_assign_agent) {
    fail(
      422,
      'agent_choice_required',
      'Pick either an agent or Auto-assign, not both'
    );
  }

  const duration =
    type === 'key_handover'
      ? body.duration_minutes
      : property.viewing_duration_minutes;
  let agentId = null;
  let autoAssigned = false;
  let endTime = null;

  if (isAgentBased) {
    const available = computeSlots(db, {
      propertyId: property.property_id,
      appointmentType: type,
      durationMinutes: duration,
      agentId: body.agent_id,
      dateStr: body.date,
    });
    const startMin = body.start_time;
    const slot = (available.slots || []).find(s => s.start_time === startMin);
    if (!slot)
      fail(409, 'slot_unavailable', 'That slot is no longer available');
    agentId = resolveAgentForCreate(
      property,
      body,
      slot.available_agent_ids,
      body.date
    );
    if (!slot.available_agent_ids.includes(agentId))
      fail(409, 'agent_unavailable', 'Agent is not free at this time');
    autoAssigned = !!body.auto_assign_agent;
    endTime = slot.end_time;
  } else {
    const available = computeSlots(db, {
      propertyId: property.property_id,
      appointmentType: type,
      dateStr: body.date,
    });
    const startMin = body.start_time;
    const inWindow = (available.arrival_windows || []).some(
      w => startMin >= w.start_time && startMin < w.end_time
    );
    if (!inWindow)
      fail(409, 'slot_unavailable', 'Outside opening hours, or in the past');
  }

  const now = new Date().toISOString();
  const appt = {
    appointment_id: newId('appt'),
    appointment_type: type,
    property_id: property.property_id,
    unit_id: body.unit_id || null,
    status: body.status || 'requested',
    date: body.date,
    start_time: body.start_time,
    end_time: endTime,
    duration_minutes: isAgentBased ? duration : null,
    agent_id: agentId,
    agent_auto_assigned: autoAssigned,
    customer_id: body.customer_id,
    customer_name: body.customer_name || null,
    customer_phone: body.customer_phone || null,
    conversation_id: body.conversation_id || null,
    location_type: body.location_type || 'showroom',
    location_note: body.location_note || null,
    source: body.source || 'portal',
    customer_notes: body.customer_notes || null,
    agent_notes: body.agent_notes || null,
    handover_checklist:
      type === 'key_handover' ? body.handover_checklist || null : null,
    before_reschedule_metadata: null,
    cancelled_at: null,
    cancellation_reason: null,
    completed_at: null,
    created_by: null,
    created_at: now,
    updated_at: now,
  };
  db.appointments.push(appt);
  return { status: 201, data: serializeAppointment(appt) };
});

route('get', '/properties/appointments/:id', params => ({
  status: 200,
  data: serializeAppointment(findAppointment(params.id)),
}));

route('patch', '/properties/appointments/:id', (params, { body }) => {
  const appt = findAppointment(params.id);
  if (body.agent_id !== undefined) {
    // A row's own persisted end_time (set only for agent-based bookings at
    // create/reschedule time) is the source of truth for "is this a
    // walk-in row", not the property's current booking_mode — an existing
    // walk-in booking stays agent-less even after the project switches to
    // Agent mode (PRD §3.5: "existing bookings stay exactly as they were
    // booked").
    if (appt.end_time === null)
      fail(
        422,
        'agent_not_applicable',
        'This appointment has no agent (walk-in)'
      );
    const property = findProperty(appt.property_id);
    if (!property.agent_ids.includes(body.agent_id))
      fail(
        422,
        'agent_not_assigned_to_property',
        'Agent not assigned to this project'
      );
    if (
      !computeSlots(db, {
        propertyId: property.property_id,
        appointmentType: appt.appointment_type,
        durationMinutes: appt.duration_minutes,
        agentId: body.agent_id,
        dateStr: appt.date,
        excludeAppointmentId: appt.appointment_id,
      }).slots?.some(s => s.start_time === appt.start_time)
    ) {
      fail(409, 'agent_unavailable', 'That agent is not free at this time');
    }
    appt.agent_id = body.agent_id;
    appt.agent_auto_assigned = false;
  }
  [
    'customer_notes',
    'agent_notes',
    'handover_checklist',
    'location_type',
    'location_note',
  ].forEach(key => {
    if (body[key] !== undefined) appt[key] = body[key];
  });
  appt.updated_at = new Date().toISOString();
  return { status: 200, data: serializeAppointment(appt) };
});

route('put', '/properties/appointments/:id/status', (params, { body }) => {
  const appt = findAppointment(params.id);
  appt.status = body.status;
  appt.updated_at = new Date().toISOString();
  if (body.status === 'cancelled') {
    appt.cancelled_at = appt.updated_at;
    appt.cancellation_reason = body.reason || null;
  }
  if (body.status === 'completed') appt.completed_at = appt.updated_at;
  return { status: 200, data: serializeAppointment(appt) };
});

route('put', '/properties/appointments/:id/reschedule', (params, { body }) => {
  const appt = findAppointment(params.id);
  const property = findProperty(appt.property_id);
  const isAgentBased =
    appt.appointment_type === 'key_handover' ||
    property.booking_mode === 'agent';

  const before = {
    booking_mode: appt.agent_id ? 'agent' : 'walk_in',
    date: appt.date,
    start_time: appt.start_time,
    end_time: appt.end_time,
    duration_minutes: appt.duration_minutes,
    agent_id: appt.agent_id,
    status: appt.status,
    reason: body.reason || null,
  };

  let agentId = null;
  let autoAssigned = false;
  let endTime = null;
  const duration =
    appt.appointment_type === 'key_handover'
      ? body.duration_minutes || appt.duration_minutes
      : property.viewing_duration_minutes;

  if (isAgentBased) {
    const available = computeSlots(db, {
      propertyId: property.property_id,
      appointmentType: appt.appointment_type,
      durationMinutes: duration,
      agentId: body.agent_id,
      dateStr: body.date,
      excludeAppointmentId: appt.appointment_id,
    });
    const slot = (available.slots || []).find(
      s => s.start_time === body.start_time
    );
    if (!slot)
      fail(409, 'slot_unavailable', 'That slot is no longer available');
    agentId = resolveAgentForCreate(
      property,
      body,
      slot.available_agent_ids,
      body.date
    );
    if (!agentId)
      fail(422, 'agent_choice_required', 'Pick an agent or Auto-assign');
    if (!slot.available_agent_ids.includes(agentId))
      fail(409, 'agent_unavailable', 'Agent is not free at this time');
    autoAssigned = !!body.auto_assign_agent;
    endTime = slot.end_time;
  } else {
    const available = computeSlots(db, {
      propertyId: property.property_id,
      appointmentType: appt.appointment_type,
      dateStr: body.date,
      excludeAppointmentId: appt.appointment_id,
    });
    const inWindow = (available.arrival_windows || []).some(
      w => body.start_time >= w.start_time && body.start_time < w.end_time
    );
    if (!inWindow)
      fail(409, 'slot_unavailable', 'Outside opening hours, or in the past');
  }

  Object.assign(appt, {
    date: body.date,
    start_time: body.start_time,
    end_time: endTime,
    duration_minutes: isAgentBased ? duration : null,
    agent_id: agentId,
    agent_auto_assigned: autoAssigned,
    status: 'rescheduled',
    before_reschedule_metadata: before,
    updated_at: new Date().toISOString(),
  });
  return { status: 200, data: serializeAppointment(appt) };
});

route('delete', '/properties/appointments/:id', params => {
  findAppointment(params.id);
  db.appointments = db.appointments.filter(a => a.appointment_id !== params.id);
  return { status: 204, data: null };
});

// -- import --
// Mirrors 10-api-reference.md §A14: one .xlsx `file`, jobs listed newest
// first. The mock doesn't parse the workbook — every upload "completes"
// with zero rows. `template` is registered before `:jobId` so it isn't
// swallowed by the id route.
route('get', '/properties/import/template', () => ({
  status: 200,
  data: new Blob([''], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }),
}));
route('get', '/properties/import', (params, { query }) => {
  const jobs = Object.values(db.importJobs)
    .filter(job => !query.status || job.status === query.status)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return { status: 200, data: paginate(jobs, query) };
});
route('post', '/properties/import', (params, { body }) => {
  const file = body instanceof FormData ? body.get('file') : null;
  if (!file) {
    fail(422, 'no_files', 'No file provided');
  }
  if (!/.xlsx$/i.test(file.name || '')) {
    fail(415, 'unsupported_file_type', 'Upload the .xlsx template');
  }
  const mode = body.get('mode') === 'replace' ? 'replace' : 'merge';
  const jobId = newId('import');
  const now = new Date().toISOString();
  db.importJobs[jobId] = {
    job_id: jobId,
    status: 'completed',
    mode,
    file_name: file.name,
    file_size: file.size,
    uploaded_by: { user_id: 1, name: 'Portal admin', email: null },
    progress: { total_rows: 0, processed_rows: 0, percentage: 100 },
    created: 0,
    updated: 0,
    failed: 0,
    skipped: 0,
    deleted: 0,
    warnings: [],
    error: null,
    rows: [],
    row_counts: {
      projects: 0,
      floor_plans: 0,
      features: 0,
      nearby: 0,
      gallery: 0,
      brochures: 0,
    },
    created_at: now,
    started_at: now,
    completed_at: now,
    file_available: true,
  };
  return {
    status: 202,
    data: {
      message:
        'Import job queued successfully. Use the job_id to check status.',
      job_id: jobId,
      status: 'queued',
      mode,
    },
  };
});
route('get', '/properties/import/:jobId', params => {
  const job = db.importJobs[params.jobId];
  if (!job) notFound('import_job_not_found');
  return { status: 200, data: job };
});
route('get', '/properties/import/:jobId/file', params => {
  const job = db.importJobs[params.jobId];
  if (!job) notFound('import_job_not_found');
  if (!job.file_available) notFound('import_file_not_found');
  return {
    status: 200,
    data: new Blob([''], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  };
});

// ---------- dispatcher ----------

function matchRoute(method, path) {
  for (let i = 0; i < routes.length; i += 1) {
    const r = routes[i];
    if (r.method !== method) continue; // eslint-disable-line no-continue
    const match = r.regex.exec(path);
    if (match) {
      const params = {};
      r.keys.forEach((key, index) => {
        params[key] = decodeURIComponent(match[index + 1]);
      });
      return { handler: r.handler, params };
    }
  }
  return null;
}

export async function mockRequest({
  method = 'get',
  path,
  params: query = {},
  data: body,
}) {
  await delay();
  const found = matchRoute(method.toLowerCase(), path);
  if (!found) {
    throw new MockHttpError(
      404,
      'not_found',
      `No mock route for ${method} ${path}`
    );
  }
  // A route handler's own fail() calls already throw MockHttpError, in
  // exactly the shape callers expect — nothing to translate here.
  const result = found.handler(found.params, { query: query || {}, body });
  return { data: result.data, status: result.status };
}
